import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class LoggerService {
  private logLevel: string;
  private logEnabled: boolean;

  constructor() {
    this.logLevel = environment.logger.level;
    this.logEnabled = environment.logger.enabled;
    this.info(`Log level: ${this.logLevel}. (constructor)`);
    this.info(`Log enabled: ${this.logEnabled}. (constructor)`);
  }

  private canLog(level: string): boolean {
    const levels = ['debug', 'info', 'warning', 'error'];
    const currentLevelIndex = levels.indexOf(this.logLevel);
    const messageLevelIndex = levels.indexOf(level);
    return (messageLevelIndex >= currentLevelIndex) && this.logEnabled;
  }

  private getFormattedTimestamp(): string {
    const options: Intl.DateTimeFormatOptions = {
      timeZoneName: 'short', // Include timezone abbreviation
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false // Use 24-hour format
    };
    return new Date().toLocaleString(undefined, options);
  }

  private formatLog(level: string, functionName: string, message: string): string {
    const timestamp = this.getFormattedTimestamp(); // Get the current timestamp
    return `${timestamp} - ${level.toUpperCase()}: ${message} (${functionName})`;
  }

  private getCallerFunctionName(): string {
    try {
      const stack = new Error().stack;
      if (stack) {
        const stackLines = stack.split('\n');
        const logIndex = stackLines.findIndex(line =>
          line.includes('debug') ||
          line.includes('info') ||
          line.includes('warn') ||
          line.includes('err')
        );

        if (logIndex >= 0 && logIndex < stackLines.length - 1) {
          let callerLine = stackLines[logIndex + 1]?.trim();
          // Remove leading "at" or "at _" or "at new _" using a regular expression
          callerLine = callerLine.replace(/^at\s+(new\s+)?_?|\n\s*at\s+(new\s+)?_?/gm, '');

          return callerLine;
        }
      }
      return 'unknown file';
    } catch (error) {
      // Log the error or handle it as needed
      console.error('Error in getCallerFunctionName:', error);
      return 'unknown file';
    }
  }

  private formatErrorDetails(error: any): string {
    if (error instanceof Error) {
      // If the error is an instance of the Error class, log its stack trace and message
      return `Message: ${error.message}\nStack: ${error.stack}`;
    } else if (typeof error === 'object') {
      // If the error is a generic object, stringify it
      return JSON.stringify(error, null, 2);
    } else {
      // If the error is a primitive value (e.g., string, number), log it directly
      return String(error);
    }
  }

  debug(message: string): void {
    const functionName = this.getCallerFunctionName();
    if (this.canLog('debug')) {
      console.log(this.formatLog('debug', functionName, message));
    }
  }

  info(message: string): void {
    const functionName = this.getCallerFunctionName();
    if (this.canLog('info')) {
      console.log(this.formatLog('info', functionName, message));
    }
  }

  warn(message: string): void;
  warn(message: string, error: any): void;
  warn(error: any): void;
  warn(arg1: string | any, arg2?: any): void {
  const functionName = this.getCallerFunctionName();
    if (this.canLog('warning')) {
      let baseLog: string;

      if (typeof arg1 === 'string') {
        // Case 1: Message and optional error object
        baseLog = this.formatLog('warning', functionName, arg1);
      } else {
        // Case 2: Only error object
        baseLog = this.formatLog('warning', functionName, 'A warning occurred');
        arg2 = arg1; // Treat first argument as the error object
      }

      if (arg2) {
        // Include error/exception details
        const errorDetails = this.formatErrorDetails(arg2);
        console.warn(`${baseLog}\nWarning Details: ${errorDetails}`);
      } else {
        // Message-only warning
        console.warn(baseLog);
      }
    }
  }

  err(message: string, error?: any): void;
  err(error: any): void;
  err(arg1: string | any, arg2?: any): void {
    const functionName = this.getCallerFunctionName();

    if (this.canLog('error')) {
      let baseLog: string;

      if (typeof arg1 === 'string') {
        // Case 1: Message and optional error object
        baseLog = this.formatLog('error', functionName, arg1);
      } else {
        // Case 2: Only error object
        baseLog = this.formatLog('error', functionName, 'An error occurred');
        arg2 = arg1; // Treat the first argument as the error object
      }

      if (arg2) {
        // If an error object is provided, include its details in the log
        const errorDetails = this.formatErrorDetails(arg2);
        console.error(`${baseLog}\nError Details: ${errorDetails}`);
      } else {
        // If no error object is provided, log the message as usual
        console.error(baseLog);
      }
    }
  }
}
